import React, { useEffect, useState } from "react";
import Calling from "../api/Calling";
import { toast } from "react-toastify";
import {
  LuChartLine,
  LuUsers,
  LuGamepad2,
  LuTrendingUp,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import MainButton from "../components/ul/MainButton";
import UserChart from "../components/charts/UserChart";
import getISTDate from "../components/getISTDate";
import toIST from "../components/toIST";

const dateRanges = [
  "today",
  "yesterday",
  "thisWeek",
  "thisMonth",
  "last6Months",
  "thisYear",
];

const Home = () => {
  const [loader, setLoader] = useState(false);
  const [userData, setUserData] = useState([]);
  const [playData, setPlayData] = useState([]);
  const [startDate, setStartDate] = useState(getISTDate());
  const [endDate, setEndDate] = useState(getISTDate());
  const [selectedBtn, setSelectedBtn] = useState("today");
  const [filteredGame, setFilteredGame] = useState("");
  const [filteredUser, setFilteredUser] = useState("");

  const [type, setType] = useState("user");
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoader(true);
    try {
      const response = await Calling.AdminDashbord(startDate, endDate, type);
      if (response?.status) {
        setUserData(response?.totalUser || []);
        setPlayData(response?.totalPlay || []);
      } else {
        toast.error(response?.message || "Failed to fetch dashboard data.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Something went wrong while fetching data.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, type]);

  const handleDateRangeFilter = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case "today":
        start = end = toIST(today);
        break;

      case "yesterday":
        const y = new Date(today);
        y.setDate(today.getDate() - 1);
        start = end = toIST(y);
        break;

      case "thisWeek":
        const f = new Date(today);
        f.setDate(today.getDate() - today.getDay());
        const l = new Date(f);
        l.setDate(f.getDate() + 6);
        start = toIST(f);
        end = toIST(l);
        break;

      case "thisMonth":
        const sm = new Date(today.getFullYear(), today.getMonth(), 1);
        const em = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        start = toIST(sm);
        end = toIST(em);
        break;

      case "last6Months":
        const s6 = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        const e6 = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        start = toIST(s6);
        end = toIST(e6);
        break;

      case "thisYear":
        const sy = new Date(today.getFullYear(), 0, 1);
        const ey = new Date(today.getFullYear(), 11, 31);
        start = toIST(sy);
        end = toIST(ey);
        break;

      default:
        start = end = "";
    }

    setSelectedBtn(range);
    setStartDate(start);
    setEndDate(end);
  };

  const handleFormDate = (e) => {
    if (e.target.name === "startDate") {
      setStartDate(e.target.value);
      setSelectedBtn("range");
    } else {
      setEndDate(e.target.value);
      setSelectedBtn("range");
    }
  };

  const filteredPlayData = playData.filter(
    (play) =>
      (filteredGame ? play.game_name === filteredGame : true) &&
      (filteredUser ? play.u_info.includes(filteredUser) : true)
  );

  const uniqueUserPlayed = [
    ...new Set(filteredPlayData.map((play) => play.u_info)),
  ].length;

  return (
    <div className="md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
          {/* Header + date range */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 pt-6 pb-5">
            <div className="flex items-center gap-4">
              <span className="p-3.5 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl shadow-lg shadow-teal-500/30 flex items-center justify-center">
                <LuChartLine className="text-2xl md:text-3xl" />
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-main-font tracking-tight leading-none">
                  Dashboard Overview
                </h1>
                <p className="text-sm text-main-subfont mt-1.5 flex items-center gap-2">
                  Track users, plays and activity at a glance
                  {loader && (
                    <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-brand-teal border-t-transparent animate-spin" />
                  )}
                </p>
              </div>
            </div>

            {/* Date inputs (desktop) */}
            <form className="hidden md:flex items-center gap-2">
              <input
                type="date"
                name="startDate"
                value={startDate}
                onChange={handleFormDate}
                className="ui-input cursor-pointer rounded-xl px-3 py-2 text-sm"
              />
              <span className="text-main-subfont text-sm">to</span>
              <input
                type="date"
                name="endDate"
                value={endDate}
                onChange={handleFormDate}
                className="ui-input cursor-pointer rounded-xl px-3 py-2 text-sm"
              />
            </form>
          </div>

          {/* Filter toolbar — slim, no box */}
          <div className="flex flex-wrap items-center gap-2 pb-5 border-b border-main-border">
            {/* Mobile: date range dropdown */}
            <div className="md:hidden w-full">
              <select
                value={selectedBtn}
                onChange={(e) => handleDateRangeFilter(e.target.value)}
                className="ui-input w-full cursor-pointer rounded-xl px-4 py-2 text-sm"
              >
                <option value="">Select Range</option>
                {dateRanges.map((range) => (
                  <option key={range} value={range}>
                    {range
                      .replace(/([A-Z0-9])/g, " $1")
                      .trim()
                      .replace(/^./, (s) => s.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Desktop: date range pills */}
            <div className="hidden md:flex flex-wrap gap-2">
              {dateRanges.map((range) => (
                <MainButton
                  key={range}
                  onClick={() => handleDateRangeFilter(range)}
                  label={range
                    .replace(/([A-Z0-9])/g, " $1")
                    .trim()
                    .replace(/^./, (s) => s.toUpperCase())}
                  selectState={selectedBtn === range}
                />
              ))}
            </div>

          </div>

          {/* Stat Cards — clean overview style */}
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-6">
            {(type === "all" || type === "user") && (
              <div
                onClick={() => navigate("/manage/user/all/data")}
                className="rounded-2xl bg-white border border-main-border shadow-sm p-5 cursor-pointer hover:border-brand-teal/40 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm text-main-subfont">New Users</span>
                  <span className="w-10 h-10 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
                    <LuUsers size={18} />
                  </span>
                </div>
                <p className="text-3xl font-extrabold text-main-font mt-4">
                  {userData.length}
                </p>
                <p className="text-xs text-main-subfont mt-1">
                  Registered in selected range
                </p>
              </div>
            )}

            {(type === "all" || type === "play") && (
              <>
                <div className="rounded-2xl bg-white border border-main-border shadow-sm p-5 hover:border-brand-teal/40 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm text-main-subfont">Total Plays</span>
                    <span className="w-10 h-10 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
                      <LuGamepad2 size={18} />
                    </span>
                  </div>
                  <p className="text-3xl font-extrabold text-main-font mt-4">
                    {playData.length}
                  </p>
                  <p className="text-xs text-main-subfont mt-1">
                    Games played in selected range
                  </p>
                </div>

                <div className="rounded-2xl bg-white border border-main-border shadow-sm p-5 hover:border-brand-teal/40 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm text-main-subfont">
                      Unique Players
                    </span>
                    <span className="w-10 h-10 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
                      <LuTrendingUp size={18} />
                    </span>
                  </div>
                  <p className="text-3xl font-extrabold text-main-font mt-4">
                    {uniqueUserPlayed}
                  </p>
                  <p className="text-xs text-main-subfont mt-1">
                    Distinct users who played
                  </p>
                </div>
              </>
            )}
          </section>

          {/* Charts */}
          {(type === "all" || type === "user") && (
            <section className="mt-6">
              <UserChart userData={userData} />
            </section>
          )}
    </div>
  );
};

export default Home;
